import {
  LYNX_VIEWBOX,
  LYNX_SOLID,
  LYNX_EYE_X,
  LYNX_IRIS_R,
  LYNX_PUPIL_R,
  LYNX_BEAK_LINE,
  LOCKUP_VIEWBOX,
  LOCKUP_MARK_TRANSFORM,
  WORDMARK_MODULAR,
  WORDMARK_IOT,
} from "./lynx-geometry";

// Re-exported so consumers that need the raw geometry (e.g. custom SVG
// compositions) can pull it through this .tsx subpath — the package's
// export map only resolves "./*" to .tsx files.
export {
  LYNX_SOLID,
  LYNX_EYE_X,
  LYNX_IRIS_R,
  LYNX_PUPIL_R,
  LYNX_BEAK_LINE,
};

// Marca Lynx en línea, consciente del tema: la tinta hereda `currentColor`
// (la define el texto que la rodea) y el iris usa el ámbar de marca, que
// cambia con .dark vía --brand-amber. Así el mismo componente sirve en
// header claro, footer oscuro y cualquier superficie intermedia.
// The --brand-amber fallback lets consumers use this without defining the
// CSS variable themselves; define it to match their own light/dark palette.
function MarkBody() {
  return (
    <>
      <path d={LYNX_SOLID} fill="currentColor" fillRule="nonzero" />
      <circle
        cx={-LYNX_EYE_X}
        cy={0}
        r={LYNX_IRIS_R}
        fill="var(--brand-amber, #e08a28)"
      />
      <circle
        cx={LYNX_EYE_X}
        cy={0}
        r={LYNX_IRIS_R}
        fill="var(--brand-amber, #e08a28)"
      />
      <circle cx={-LYNX_EYE_X} cy={0} r={LYNX_PUPIL_R} fill="currentColor" />
      <circle cx={LYNX_EYE_X} cy={0} r={LYNX_PUPIL_R} fill="currentColor" />
      <path d={LYNX_BEAK_LINE} fill="currentColor" />
    </>
  );
}

export function LynxMark({
  className,
  ...props
}: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox={LYNX_VIEWBOX}
      className={className}
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      <MarkBody />
    </svg>
  );
}

// Solo el wordmark kerneado (sin marca), para cuando el Lynx ya está en escena.
export function LynxWordmark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="330 70 690 120"
      className={className}
      role="img"
      aria-label="ModularIoT"
    >
      <path d={WORDMARK_MODULAR} fill="currentColor" />
      <path d={WORDMARK_IOT} fill="var(--brand-amber, #e08a28)" />
    </svg>
  );
}

// Ícono + wordmark como elementos independientes (no el lockup de un solo
// SVG): permite ajustar el tamaño del ícono sin afectar el del texto, algo
// que el viewBox combinado de LynxLockup no permite.
export function LynxBrand({
  className = "",
  iconClassName = "h-9 w-9",
  wordmarkClassName = "h-5 w-auto",
}: {
  className?: string;
  iconClassName?: string;
  wordmarkClassName?: string;
}) {
  return (
    <span className={`flex shrink-0 items-center gap-2 ${className}`}>
      <LynxMark className={iconClassName} />
      <LynxWordmark className={wordmarkClassName} />
    </span>
  );
}

// Lockup completo (marca + wordmark kerneado, Opción B: "Modular" en tinta,
// "IoT" en ámbar — resuelve la ambigüedad I/l y hace intencional el ámbar).
export function LynxLockup({ className }: { className?: string }) {
  return (
    <svg
      viewBox={LOCKUP_VIEWBOX}
      className={className}
      role="img"
      aria-label="ModularIoT"
    >
      <g transform={LOCKUP_MARK_TRANSFORM}>
        <MarkBody />
      </g>
      <path d={WORDMARK_MODULAR} fill="currentColor" />
      <path d={WORDMARK_IOT} fill="var(--brand-amber, #e08a28)" />
    </svg>
  );
}
